import { BigCommerceSortKeys, BKDFSortKeys, BKDFToBigCommerceSortKeys } from "../../../../config"

type ProductsList = { productId: number; productData: BigCommerceProduct }[]

const BKDFFromBigCommerceLineItems = (lineItems: BigCommerceCart["lineItems"]) => {
    const { physicalItems, digitalItems, customItems } = lineItems
    const cartItemMapper = ({ entityId, quantity, productEntityId }: PhysicalItem | CartCustomItem) => ({
        merchandiseId: productEntityId ? productEntityId.toString() : entityId.toString(),
        quantity,
    })

    return [physicalItems, digitalItems, customItems].flatMap((list) => list.map(cartItemMapper))
}

const bigCommerceToBKDFOptions = (options: BigCommerceProductOption[]): BKDFProductOption[] => {
    return options.map((option) => {
        return {
            id: option.entityId.toString(),
            name: option.displayName.toString(),
            values: option.values ? option.values.edges.map(({ node: value }) => value.label) : [],
        }
    })
}
const bigCommerceToBKDFVariants = (variants: BigCommerceProductVariant[], productId: number): BKDFProductVariant[] => {
    return variants.map((variant) => {
        return {
            parentId: productId.toString(),
            id: variant.entityId.toString(),
            sku: variant.sku,
            title: "",
            availableForSale: variant.isPurchasable,
            defaultImage: variant.defaultImage,
            selectedOptions: variant.options?.edges.map(({ node: option }) => ({
                name: option.displayName ?? "",
                value: option.values.edges.map(({ node }) => node.label)[0] ?? "",
            })) || [
                {
                    name: "",
                    value: "",
                },
            ],
            price: {
                amount:
                    variant.prices?.price.value.toString() || variant.prices?.priceRange.max.value.toString() || "0",
                currencyCode: variant.prices?.price.currencyCode || variant.prices?.priceRange.max.currencyCode || "",
            },
        }
    })
}

const bigCommerceToBKDFProduct = (product: BigCommerceProduct): BKDFProduct => {
    const createBKDFProductImage = (img: { url: string; altText: string }) => {
        return {
            url: img ? img.url : "",
            altText: img ? img.altText : "",
            width: 2048,
            height: 2048,
        }
    }
    const options = product.productOptions.edges.length
        ? bigCommerceToBKDFOptions(product.productOptions.edges.map((item: { node: any }) => item.node))
        : []
    const variants = product.variants.edges.length
        ? bigCommerceToBKDFVariants(
              product.variants.edges.map((item: { node: any }) => item.node),
              product.entityId
          )
        : []

    return {
        id: product.entityId.toString(),
        sku: product.sku,
        categories: product.categories.edges.map(({ node }) => ({ name: node.name, description: node.description })),
        handle: product.path,
        availableForSale: product.availabilityV2.status === "Available" ? true : false,
        title: product.name,
        description: product.plainTextDescription || "",
        descriptionHtml: product.description ?? "",
        options,
        priceRange: {
            maxVariantPrice: {
                amount: product.prices.priceRange.max.value.toString() || product.prices.price.value.toString() || "0",
                currencyCode: product.prices.priceRange.max.currencyCode || product.prices.price.currencyCode || "",
            },
            minVariantPrice: {
                amount: product.prices.priceRange.min.value.toString() || product.prices.price.value.toString() || "0",
                currencyCode: product.prices.priceRange.min.currencyCode || product.prices.price.currencyCode || "",
            },
        },
        salePrice: {
            amount: product.prices.retailPrice?.value.toString() || "",
            currencyCode: product.prices.retailPrice?.currencyCode || "",
        },
        variants,
        reviews: {
            avgRating: product?.reviews?.edges?.length
                ? product.reviews.edges.map(({ node }) => node.rating).reduce((sum, rating) => sum + rating, 0)
                : null,
        },
        images: product.images ? product.images.edges.map(({ node: img }) => createBKDFProductImage(img)) : [],
        featuredImage: createBKDFProductImage(product.defaultImage),
        seo: {
            title: product.seo.pageTitle || product.name,
            description: product.seo.metaDescription || "",
        },
        tags: [product.seo.metaKeywords] || [],
        updatedAt: product.createdAt.utc.toString(),
    }
}

const bigCommerceToBKDFProducts = (products: BigCommerceProduct[]) => {
    const reshapedProducts = []

    for (const product of products) {
        if (product) {
            const reshapedProduct = bigCommerceToBKDFProduct(product)

            if (reshapedProduct) {
                reshapedProducts.push(reshapedProduct)
            }
        }
    }

    return reshapedProducts
}

const bigCommerceToBKDFCartItems = (lineItems: BigCommerceCart["lineItems"], products: ProductsList) => {
    const getItemMapper = (products: ProductsList) => {
        return (item: PhysicalItem): BKDFCartItem => {
            const BKDFProductFallback = {
                id: "",
                handle: "",
                availableForSale: false,
                title: "",
                description: "",
                descriptionHtml: "",
                options: [],
                priceRange: {
                    maxVariantPrice: { amount: "", currencyCode: "" },
                    minVariantPrice: { amount: "", currencyCode: "" },
                },
                variants: [],
                featuredImage: {
                    url: "",
                    altText: "",
                    width: 0,
                    height: 0,
                },
                images: [
                    {
                        url: "",
                        altText: "",
                        width: 0,
                        height: 0,
                    },
                ],
                seo: { title: "", description: "" },
                tags: [],
                updatedAt: "",
                sku: "",
                categories: [],
            }
            let product: BKDFProduct
            let selectedOptions

            const productData = products.filter(
                ({ productId }) => productId === (item as PhysicalItem).productEntityId
            )[0]?.productData

            product = productData ? bigCommerceToBKDFProduct(productData) : BKDFProductFallback
            return {
                id: item.entityId.toString(), // NOTE: used as lineId || lineItemId
                quantity: item.quantity,
                cost: {
                    totalAmount: {
                        amount: item.extendedListPrice.value.toString() || item.listPrice.value.toString() || "0",
                        currencyCode: item.extendedListPrice.currencyCode || item.listPrice.currencyCode || "",
                    },
                },
                merchandise: {
                    id: (item as PhysicalItem).variantEntityId!.toString(),
                    title: `${item.name}`,
                    selectedOptions:
                        product.variants?.find((v) => v.id === (item as PhysicalItem)?.variantEntityId?.toString())
                            ?.selectedOptions || [],
                    product,
                },
            }
        }
    }
    const { physicalItems } = lineItems
    const line1 = physicalItems.map((item) => getItemMapper(products)(item))
    return [...line1]
}

const bigCommerceToBKDFCart = (cart: BigCommerceCart, products: ProductsList, checkoutUrl?: string): BKDFCart => {
    return {
        id: cart.entityId,
        checkoutUrl: checkoutUrl ?? "",
        cost: {
            baseAmount: { amount: String(cart.baseAmount.value), currencyCode: cart.currencyCode },
            discountedAmount: { amount: String(cart.discountedAmount.value), currencyCode: cart.currencyCode },
            amount: { amount: String(cart.amount.value), currencyCode: cart.currencyCode },
            discounts: cart.discounts.map((d) => ({
                entityId: d.entityId,
                discountedAmount: { amount: String(d.discountedAmount.value), currencyCode: cart.currencyCode },
            })),
        },
        lines: bigCommerceToBKDFCartItems(cart.lineItems, products),
        totalQuantity: cart.lineItems.totalQuantity,
    }
}

const bigCommerceToBKDFCollection = (collection: BigCommerceCollection): BKDFCollection => {
    if (!collection) {
        return {
            handle: "",
            title: "",
            description: "",
            seo: {
                title: "",
                description: "",
            },
            updatedAt: "",
            path: "",
        }
    }

    return {
        handle: collection.entityId.toString() || collection.name,
        title: collection.name,
        description: collection.description,
        seo: {
            title: collection.seo.pageTitle,
            description: collection.seo.metaDescription,
        },
        updatedAt: new Date().toISOString(),
        path: `/search${collection.path}`,
    }
}

export {
    bigCommerceToBKDFCart,
    bigCommerceToBKDFCollection,
    bigCommerceToBKDFProduct,
    bigCommerceToBKDFProducts,
    BKDFFromBigCommerceLineItems,
}

export const BKDFToBigCommerceSorting = (
    isReversed: boolean,
    sortKey?: string
): keyof typeof BigCommerceSortKeys | null => {
    const BKDFSorting: Record<string, string> = {
        RELEVANCE: "RELEVANCE",
        BEST_SELLING: "BEST_SELLING",
        CREATED_AT: "CREATED_AT",
        PRICE: "PRICE",
    }

    if (!sortKey || BKDFSorting[sortKey] === undefined) {
        return null
    }

    if (sortKey === BKDFSortKeys.PRICE) {
        return isReversed ? BKDFToBigCommerceSortKeys.PRICE_ON_REVERSE : BKDFToBigCommerceSortKeys.PRICE
    }

    return BKDFToBigCommerceSortKeys[sortKey as keyof typeof BKDFSortKeys]
}

export const bigCommerceToBKDFPageContent = (page: BigCommercePage): BKDFPage => {
    return {
        id: page.entityId.toString(),
        title: page.name,
        handle: page.path.slice(1),
        body: page.htmlBody ?? "",
        bodySummary: page.plainTextSummary ?? "",
        seo: {
            title: page.seo.pageTitle,
            description: page.seo.metaDescription,
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
    }
}
